import { useState } from "react";
import { Link } from "react-router-dom";
import {
  ChartLineUp, ShoppingCart, Package, ShieldCheck, Sparkle, UsersThree,
  CaretDown, ArrowRight, Notebook, Warning, Check, Globe,
} from "@phosphor-icons/react";
import { PricingCards } from "../components/PricingCards";

const FEATURES = [
  { icon: ChartLineUp, title: "Dashboard en temps réel", desc: "Bénéfice net, trésorerie disponible, ce qu'on vous doit et ce que vous devez — en un coup d'œil." },
  { icon: ShoppingCart, title: "Ventes simplifiées", desc: "Recherche d'un produit en deux lettres, encaissement comptant ou à crédit, ticket imprimable aussitôt." },
  { icon: Package, title: "Stock intelligent", desc: "Alerte avant la rupture, pas après. Réceptions et corrections d'inventaire clairement séparées." },
  { icon: ShieldCheck, title: "Traçabilité anti-fraude", desc: "Chaque annulation, chaque remise, chaque correction laisse une trace nominative. Personne n'y échappe, pas même le patron.", highlight: true },
  { icon: Sparkle, title: "Assistant IA", desc: "« Combien j'ai vendu aujourd'hui ? » — une question en français simple, une réponse immédiate." },
  { icon: UsersThree, title: "Équipe et permissions", desc: "Chaque employé voit exactement ce que vous décidez, module par module, modifiable à tout moment." },
  { icon: Globe, title: "Multi-pays et multi-devises", desc: "Guinée, Sénégal, Mali, Côte d'Ivoire… Travaillez en GNF, Franc CFA, euro ou dollar : toute votre activité s'affiche dans votre devise." },
];

const FAQ = [
  ["Puis-je changer d'offre plus tard ?",
   "Oui, à tout moment. Les nouveaux modules apparaissent immédiatement dans votre compte, sans aucune perte de données et sans migration."],
  ["Mes données sont-elles sécurisées ?",
   "Chaque entreprise est isolée : personne d'autre n'accède à vos chiffres. Les mots de passe sont chiffrés et vous seul connaissez le vôtre — même vos employés définissent le leur eux-mêmes. Aucune donnée sensible n'est supprimée silencieusement : tout reste traçable."],
  ["Faut-il une connexion internet stable ?",
   "Kavizio est conçu pour des connexions modestes : pages légères, très peu d'images, et l'essentiel de l'interface se charge une seule fois. Une connexion mobile standard suffit."],
  ["Combien de temps pour démarrer ?",
   "Une quinzaine de minutes : vous créez votre compte, saisissez votre trésorerie de départ et vos produits déjà en stock, et vous encaissez votre première vente."],
];

function Nav() {
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-50 bg-[#F6F4EF]/90 backdrop-blur-md border-b border-[#E9E4DA]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="landing-wordmark" className="font-display font-bold text-2xl lowercase tracking-tight">kavizio</Link>
        <nav className="hidden md:flex items-center gap-7 text-sm text-[#16233D]/75">
          <a href="#fonctionnalites" data-testid="nav-features" className="hover:text-[#C9793A] transition-colors">Fonctionnalités</a>
          <a href="#tarifs" data-testid="nav-pricing" className="hover:text-[#C9793A] transition-colors">Tarifs</a>
          <a href="#faq" data-testid="nav-faq" className="hover:text-[#C9793A] transition-colors">Questions</a>
          <Link to="/contact" data-testid="nav-contact" className="hover:text-[#C9793A] transition-colors">Contact</Link>
        </nav>
        <div className="hidden md:flex items-center gap-2">
          <Link to="/connexion" data-testid="nav-login-btn" className="btn-ghost px-4 py-2 text-sm">Connexion</Link>
          <Link to="/inscription" data-testid="nav-start-btn" className="btn-amber px-5 py-2 text-sm">Commencer</Link>
        </div>
        <button data-testid="nav-mobile-toggle" onClick={() => setOpen((o) => !o)}
          className="md:hidden btn-ghost px-3 py-2 text-sm">Menu</button>
      </div>
      {open && (
        <div data-testid="nav-mobile-menu" className="md:hidden border-t border-[#E9E4DA] bg-white px-4 py-3 space-y-2">
          {[["#fonctionnalites", "Fonctionnalités"], ["#tarifs", "Tarifs"], ["#faq", "Questions"]].map(([h, l]) => (
            <a key={h} href={h} onClick={() => setOpen(false)} className="block py-2 text-sm">{l}</a>
          ))}
          <Link to="/contact" onClick={() => setOpen(false)} className="block py-2 text-sm">Contact</Link>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Link to="/connexion" className="btn-ghost py-2.5 text-center text-sm">Connexion</Link>
            <Link to="/inscription" className="btn-amber py-2.5 text-center text-sm">Commencer</Link>
          </div>
        </div>
      )}
    </header>
  );
}

function DashboardPreview() {
  return (
    <div data-testid="hero-preview" className="rounded-[22px] bg-white border border-[#E9E4DA] p-4 sm:p-5 shadow-[0_18px_50px_-18px_rgba(22,35,61,0.28)]">
      <div className="rounded-[16px] p-4 text-white mb-3" style={{ background: "#16233D" }}>
        <p className="text-[10px] uppercase tracking-wider text-white/55">Bénéfice net</p>
        <p className="font-display font-bold text-2xl sm:text-3xl tabular-nums">32 895 310 GNF</p>
        <p className="text-[11px] text-white/45 mt-0.5">20 ventes sur les 30 derniers jours</p>
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[["Chiffre d'affaires", "52 420 000"], ["Trésorerie", "32 355 310"], ["On vous doit", "5 540 000"], ["Vous devez", "1 000 000"]].map(([l, v]) => (
          <div key={l} className="rounded-[14px] bg-[#F6F4EF] px-3 py-2.5">
            <p className="text-[10px] uppercase tracking-wider text-[#16233D]/50">{l}</p>
            <p className="font-display font-bold text-sm sm:text-base tabular-nums">{v} GNF</p>
          </div>
        ))}
      </div>
      <div className="rounded-[14px] bg-[#16233D] text-white/90 px-3 py-2.5 text-xs flex gap-2 mb-2">
        <Sparkle size={15} weight="fill" color="#C9793A" className="shrink-0 mt-0.5" />
        <span>Ventes stables cette semaine ; attention, 5 540 000 GNF de créances dont une de plus de 30 jours.</span>
      </div>
      <div className="rounded-[14px] bg-[#F6E3DF] text-[#B4432F] px-3 py-2.5 text-xs flex gap-2">
        <Warning size={15} weight="fill" className="shrink-0 mt-0.5" />
        <span>Vente sous prix catalogue : Riz local 50kg à 400 000 GNF — motif enregistré, auteur identifié.</span>
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="kcard overflow-hidden">
      <button data-testid="faq-question" onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 text-left px-5 py-4 hover:bg-[#F6F4EF]/70 transition-colors">
        <span className="font-display font-semibold text-base">{q}</span>
        <CaretDown size={18} className={`shrink-0 text-[#C9793A] transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p data-testid="faq-answer" className="px-5 pb-4 text-sm text-[#16233D]/75 leading-relaxed">{a}</p>}
    </div>
  );
}

export default function Landing() {
  return (
    <div className="min-h-screen">
      <Nav />

      <section className="max-w-6xl mx-auto px-4 md:px-6 pt-12 pb-16 md:pt-20 md:pb-24 grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
        <div className="fade-in">
          <span className="inline-block text-xs font-semibold uppercase tracking-wider text-[#C9793A] bg-[#C9793A]/10 px-3 py-1.5 rounded-full mb-5">
            Conçu pour les entreprises guinéennes
          </span>
          <h1 data-testid="hero-title" className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Sachez exactement où va votre argent — et qui y touche.
          </h1>
          <p className="text-base md:text-lg text-[#16233D]/75 leading-relaxed mt-5 max-w-xl">
            Kavizio enregistre vos ventes, votre stock, vos dépenses et vos créances en temps réel,
            et garde une trace nominative de chaque correction. Plus de fuite invisible, plus de cahier à recouper le soir.
          </p>
          <p data-testid="hero-multicurrency" className="text-sm text-[#16233D]/70 mt-5">
            Disponible en Guinée et dans toute l'Afrique de l'Ouest francophone — GNF, Franc CFA, EUR, USD.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/inscription" data-testid="hero-primary-cta" className="btn-amber px-7 py-3.5 text-base">Commencer maintenant</Link>
            <a href="#demo" data-testid="hero-secondary-cta" className="btn-ghost px-7 py-3.5 text-base flex items-center gap-2">
              Voir le produit <ArrowRight size={17} />
            </a>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-2 mt-7 text-sm text-[#16233D]/60">
            {["Sans engagement", "Mise en route en 15 minutes", "Fonctionne sur téléphone"].map((t) => (              <span key={t} className="flex items-center gap-1.5"><Check size={15} weight="bold" color="#2E7D5B" /> {t}</span>
            ))}
          </div>
        </div>
        <div id="demo" className="fade-in"><DashboardPreview /></div>
      </section>

      <section className="bg-[#16233D] text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6 grid md:grid-cols-2 gap-10">
          <div>
            <Notebook size={30} color="#C9793A" weight="fill" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-4 mb-4">Le cahier et Excel vous coûtent plus cher que vous ne le pensez</h2>
            <ul className="space-y-3 text-white/70 text-sm md:text-base">
              {["Vous ne savez ce que vous avez gagné qu'à la fin du mois, quand c'est trop tard pour réagir.",
                "Les créances s'accumulent : vous oubliez qui vous doit quoi, et depuis combien de temps.",
                "Un article vendu sous le prix, un stock qui « disparaît », une vente annulée : rien ne laisse de trace.",
                "Deux cahiers qui ne tombent pas d'accord, et une soirée entière à recouper les chiffres."].map((t) => (
                <li key={t} className="flex gap-2.5"><span className="text-[#C9793A] mt-1">—</span><span>{t}</span></li>
              ))}
            </ul>
          </div>
          <div className="rounded-[22px] bg-white/5 border border-white/10 p-6 md:p-7">
            <ShieldCheck size={30} color="#C9793A" weight="fill" />
            <h2 className="font-display font-bold text-2xl sm:text-3xl mt-4 mb-4">Kavizio répond exactement à ça</h2>
            <ul className="space-y-3 text-white/80 text-sm md:text-base">
              {["Votre bénéfice, votre trésorerie et vos créances, mis à jour à chaque vente.",
                "Chaque client et chaque fournisseur identifié par son nom et son téléphone, avec ce qu'il reste à régler.",
                "Toute vente sous le prix catalogue exige un motif, et vous alerte immédiatement.",
                "Aucune donnée sensible ne s'efface : tout se corrige avec un motif, un auteur et une date."].map((t) => (
                <li key={t} className="flex gap-2.5"><Check size={17} weight="bold" color="#C9793A" className="shrink-0 mt-1" /><span>{t}</span></li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <section id="fonctionnalites" className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center">Tout ce qu'il faut pour tenir votre entreprise</h2>
        <p className="text-center text-[#16233D]/65 mt-3 mb-10 max-w-2xl mx-auto">Rien de superflu : chaque écran répond à une question que vous vous posez déjà tous les jours.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map(({ icon: Icon, title, desc, highlight }) => (
            <div key={title} data-testid={`feature-card-${title}`}
              className={`rounded-[20px] p-6 border transition-shadow hover:shadow-[0_8px_26px_-8px_rgba(22,35,61,0.16)] ${
                highlight ? "bg-[#16233D] text-white border-[#16233D]" : "bg-white border-[#E9E4DA]"}`}>
              <Icon size={26} weight="fill" color={highlight ? "#C9793A" : "#16233D"} />
              <h3 className="font-display font-bold text-lg mt-3.5 mb-1.5">{title}</h3>
              <p className={`text-sm leading-relaxed ${highlight ? "text-white/70" : "text-[#16233D]/70"}`}>{desc}</p>
              {highlight && <span className="inline-block mt-3 text-xs font-semibold text-[#C9793A]">Ce qui nous différencie</span>}
            </div>
          ))}
        </div>
      </section>

      <section id="tarifs" className="bg-white/60 border-y border-[#E9E4DA] py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center">Un tarif clair, sans surprise</h2>
          <p className="text-center text-[#16233D]/65 mt-3 mb-10 max-w-2xl mx-auto">
            Payez pour la taille de votre équipe, pas pour des fonctions que vous n'utilisez pas. Changement d'offre possible à tout moment.
          </p>
          <PricingCards />
        </div>
      </section>

      {/* TODO: à remplir avec de vrais témoignages clients (photo, nom, entreprise, citation). Aucun contenu fictif ici. */}
      <section className="max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-24">
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center">Ils nous font confiance</h2>
        <p className="text-center text-[#16233D]/65 mt-3 mb-10">Les premiers témoignages de nos clients seront publiés ici.</p>
        <div data-testid="testimonials-placeholders" className="grid sm:grid-cols-3 gap-4">
          {[0, 1, 2].map((i) => (
            <div key={i} data-testid="testimonial-placeholder" className="kcard p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-11 h-11 rounded-full bg-[#F6F4EF] border border-dashed border-[#D6D0C4]" aria-hidden />
                <div className="space-y-1.5">
                  <div className="h-3 w-24 rounded bg-[#F6F4EF] border border-dashed border-[#D6D0C4]" aria-hidden />
                  <div className="h-2.5 w-16 rounded bg-[#F6F4EF] border border-dashed border-[#D6D0C4]" aria-hidden />
                </div>
              </div>
              <p className="text-sm text-[#16233D]/40 italic">Emplacement réservé à un témoignage client authentique.</p>
            </div>
          ))}
        </div>
      </section>

      <section id="faq" className="max-w-3xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
        <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight text-center mb-8">Questions fréquentes</h2>
        <div className="space-y-2.5">
          {FAQ.map(([q, a]) => <FaqItem key={q} q={q} a={a} />)}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 md:px-6 pb-16 md:pb-24">
        <div className="rounded-[22px] p-8 md:p-12 text-center text-white" style={{ background: "#16233D" }}>
          <h2 className="font-display font-bold text-2xl sm:text-3xl lg:text-4xl tracking-tight">Reprenez le contrôle dès cette semaine</h2>
          <p className="text-white/70 mt-3 mb-7 max-w-xl mx-auto">Créez votre entreprise sur Kavizio en quelques minutes et enregistrez votre première vente aujourd'hui.</p>
          <Link to="/inscription" data-testid="footer-cta" className="btn-amber inline-block px-8 py-3.5 text-base">Commencer maintenant</Link>
        </div>
      </section>

      <footer className="border-t border-[#E9E4DA] py-10">
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex flex-col md:flex-row gap-6 md:items-center md:justify-between">
          <div>
            <p className="font-display font-bold text-2xl lowercase tracking-tight">kavizio</p>
            <p className="text-sm text-[#16233D]/55">Votre entreprise, sous contrôle.</p>
          </div>
          <nav className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#16233D]/70">
            <a href="#tarifs" className="hover:text-[#C9793A]">Tarifs</a>
            <a href="#fonctionnalites" className="hover:text-[#C9793A]">Fonctionnalités</a>
            <Link to="/contact" className="hover:text-[#C9793A]">Contact</Link>
            <Link to="/connexion" className="hover:text-[#C9793A]">Connexion</Link>
          </nav>
          <p className="text-sm text-[#16233D]/60">
            support@kavizio.com<br />+224 620 00 00 00
          </p>
        </div>
      </footer>
    </div>
  );
}
